/**
 * DoqSolvPanel.js
 *
 * Main entry component for the DoqSolv Document Management System.
 *
 * - **Documents** tab (default): Lists all generated documents with actions.
 * - **Admin** view: Full CRUD on global (default) templates.
 * - **Client** view: "Default Templates" (read‑only, clone) and
 *   "Your Templates" (full CRUD + WYSIWYG editor).
 *
 * Sits inside the Dashboard like any other panel (Orders, Invoices…).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../Auth/AuthContext';
import { apiCall } from '../../utils/apiUtils';
import Tabs from '../Common/Tabs';
import TemplateEditor from './TemplateEditor';
import TemplateFiller from './TemplateFiller';
import DocumentList from './DocumentList';
import ServiceUpgradeRequired from '../Common/ServiceUpgradeRequired';
import { toast } from 'react-toastify';

const API_BASE = '/api/external/v1/doqsolv';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const truncate = (str, len = 80) =>
  str && str.length > len ? str.slice(0, len) + '…' : str;

const categoryColors = {
  Deed: 'bg-emerald-100 text-emerald-700',
  'Rent Agreement': 'bg-blue-100 text-blue-700',
  Affidavit: 'bg-amber-100 text-amber-700',
  MOU: 'bg-purple-100 text-purple-700',
  default: 'bg-gray-100 text-gray-600',
};

const getCategoryColor = (cat) =>
  categoryColors[cat] || categoryColors.default;

// ─── Template Card ────────────────────────────────────────────────────────────

const TemplateCard = ({
  template,
  isAdmin,
  isGlobal,
  onEdit,
  onDelete,
  onClone,
  onPreview,
  onUse,
}) => {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm(`Delete template "${template.name}"?`)) return;
    setDeleting(true);
    try {
      await apiCall(`${API_BASE}/templates/${template.id}`, { method: 'DELETE' });
      toast.success('Template deleted');
      onDelete(template.id);
    } catch (err) {
      toast.error(err.message || 'Failed to delete template');
    } finally {
      setDeleting(false);
    }
  };

  const handleClone = async () => {
    try {
      const res = await apiCall(`${API_BASE}/templates/clone`, {
        method: 'POST',
        data: { source_template_id: template.id },
      });
      toast.success(`Cloned as "${res.template.name}"`);
      if (onClone) onClone(res.template);
    } catch (err) {
      toast.error(err.message || 'Clone failed');
    }
  };

  const variableCount = (template.variables || []).length;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 p-5 flex flex-col justify-between">
      {/* Header */}
      <div>
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-base font-semibold text-gray-900 leading-tight">
            {template.name}
          </h3>
          {template.category && (
            <span
              className={`ml-2 flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${getCategoryColor(
                template.category,
              )}`}
            >
              {template.category}
            </span>
          )}
        </div>

        {template.description && (
          <p className="text-sm text-gray-500 mb-3">
            {truncate(template.description, 120)}
          </p>
        )}

        {/* Meta pills */}
        <div className="flex flex-wrap gap-2 text-xs text-gray-400 mb-3">
          <span className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            {variableCount} variable{variableCount !== 1 && 's'}
          </span>
          {template.scope === 'global' && (
            <span className="bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded font-medium">
              Default
            </span>
          )}
          {template.cloned_from && (
            <span className="bg-yellow-50 text-yellow-600 px-1.5 py-0.5 rounded font-medium">
              Cloned
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
        <button
          onClick={() => onPreview(template)}
          className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100 transition"
          title="Preview"
        >
          <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          Preview
        </button>

        {/* Use / Fill Template */}
        <button
          onClick={() => onUse(template.id)}
          className="text-xs text-green-600 hover:text-green-800 px-2 py-1 rounded hover:bg-green-50 transition font-medium"
          title="Fill and generate document"
        >
          <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Use
        </button>

        {/* Clone (only shown in Default tab for clients) */}
        {isGlobal && !isAdmin && (
          <button
            onClick={handleClone}
            className="text-xs text-indigo-600 hover:text-indigo-800 px-2 py-1 rounded hover:bg-indigo-50 transition"
            title="Clone to Your Templates"
          >
            <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Clone
          </button>
        )}

        {/* Edit (admin on any, client on own) */}
        {(isAdmin || !isGlobal) && (
          <button
            onClick={() => onEdit(template)}
            className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50 transition"
            title="Edit"
          >
            <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit
          </button>
        )}

        {(isAdmin || !isGlobal) && (
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 transition ml-auto"
            title="Delete"
          >
            {deleting ? '…' : (
              <>
                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Empty State ──────────────────────────────────────────────────────────────

const EmptyState = ({ message, actionLabel, onAction }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
    <p className="text-gray-500 mb-4">{message}</p>
    {actionLabel && (
      <button onClick={onAction} className="btn-primary px-4 py-2 text-sm rounded-lg">
        {actionLabel}
      </button>
    )}
  </div>
);
// ─── Preview Overlay ──────────────────────────────────────────────────────────

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PreviewOverlay = ({ template, onClose }) => {
  const [pageImages, setPageImages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(false);

  const isPdf = template.template_type === 'pdf_overlay';

  useEffect(() => {
    if (!isPdf || !template.source_pdf_id) return;
    let cancelled = false;
    setLoadingPages(true);
    apiCall(`${API_BASE}/assets/${template.source_pdf_id}/pages`)
      .then((res) => { if (!cancelled) setPageImages(res.pages || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingPages(false); });
    return () => { cancelled = true; };
  }, [isPdf, template.source_pdf_id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-[800px] max-h-[80vh] overflow-auto p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl font-bold"
        >
          ×
        </button>
        <h3 className="text-xl font-bold text-gray-900 mb-1">{template.name}</h3>
        <p className="text-sm text-gray-500 mb-4">{template.description}</p>

        {isPdf ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs px-2 py-1 rounded bg-indigo-100 text-indigo-700 font-medium">
                PDF Overlay
              </span>
              <span className="text-xs text-gray-400">
                {(template.fields || []).length} field(s) configured
              </span>
            </div>
            {loadingPages ? (
              <div className="flex justify-center py-10">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500" />
              </div>
            ) : pageImages.length > 0 ? (
              pageImages.map((p) => (
                <img
                  key={p.page}
                  src={`${BACKEND_URL}${p.image_url}`}
                  alt={`Page ${p.page}`}
                  className="w-full border border-gray-200 rounded-lg shadow-sm"
                />
              ))
            ) : (
              <p className="text-sm text-gray-400 text-center py-10">No source PDF configured</p>
            )}
          </div>
        ) : (
          <div
            className="prose max-w-none border border-gray-200 rounded-lg p-6"
            dangerouslySetInnerHTML={{ __html: template.html_content }}
          />
        )}
      </div>
    </div>
  );
};

// ─── Main Panel ───────────────────────────────────────────────────────────────

const DoqSolvPanel = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Service guard: exempt admin, vendor, accountant
  const isExempt = isAdmin || user?.role === 'vendor' || user?.role === 'accountant';
  const isServiceEnabled = isExempt || user?.api_services?.doqsolv?.enabled;

  // State
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('documents');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTemplate, setEditorTemplate] = useState(null); // null = new
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [fillerTemplateId, setFillerTemplateId] = useState(null);

  // ── Fetch templates ──────────────────────────────────────────────────────

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const scopeParam = isAdmin
        ? ''
        : '';
      const res = await apiCall(`${API_BASE}/templates?limit=200${scopeParam}`);
      setTemplates(res.templates || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // ── Filtered lists ──────────────────────────────────────────────────────

  const globalTemplates = templates.filter((t) => t.scope === 'global');
  const clientTemplates = templates.filter((t) => t.scope === 'client');

  const filterBySearch = (list) => {
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.category || '').toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q),
    );
  };

  const displayedTemplates = (() => {
    if (isAdmin) {
      if (activeTab === 'all') return filterBySearch(templates);
      if (activeTab === 'global') return filterBySearch(globalTemplates);
      return filterBySearch(clientTemplates);
    }
    // Client view
    if (activeTab === 'defaults') return filterBySearch(globalTemplates);
    return filterBySearch(clientTemplates);
  })();

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleEdit = (template) => {
    setEditorTemplate(template);
    setEditorOpen(true);
  };

  const handleNew = () => {
    setEditorTemplate(null);
    setEditorOpen(true);
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setEditorTemplate(null);
  };

  const handleEditorSave = () => {
    handleEditorClose();
    fetchTemplates();
  };

  const handleDelete = (deletedId) => {
    setTemplates((prev) => prev.filter((t) => t.id !== deletedId));
  };

  const handleClone = (clonedTemplate) => {
    setTemplates((prev) => [clonedTemplate, ...prev]);
    // Automatically switch to "Your Templates" tab for the client
    if (!isAdmin) setActiveTab('yours');
  };

  // ── Tabs config ──────────────────────────────────────────────────────────

  const tabs = isAdmin
    ? [
        { id: 'documents', label: 'Documents', icon: '📋' },
        { id: 'all', label: 'All Templates', icon: '📄', badge: templates.length ? { text: templates.length, color: 'bg-gray-100 text-gray-600' } : null },
        { id: 'global', label: 'Global Defaults', icon: '🌐', badge: globalTemplates.length ? { text: globalTemplates.length, color: 'bg-indigo-100 text-indigo-600' } : null },
        { id: 'client', label: 'Client Templates', icon: '👤', badge: clientTemplates.length ? { text: clientTemplates.length, color: 'bg-green-100 text-green-600' } : null },
      ]
    : [
        { id: 'documents', label: 'Documents', icon: '📋' },
        { id: 'defaults', label: 'Default Templates', icon: '🌐', badge: globalTemplates.length ? { text: globalTemplates.length, color: 'bg-indigo-100 text-indigo-600' } : null },
        { id: 'yours', label: 'Your Templates', icon: '📝', badge: clientTemplates.length ? { text: clientTemplates.length, color: 'bg-green-100 text-green-600' } : null },
      ];

  // ── Render ───────────────────────────────────────────────────────────────

  // Service guard: show upgrade CTA if service not enabled
  if (!isServiceEnabled) {
    return (
      <ServiceUpgradeRequired
        serviceName="DoqSolv"
        serviceId="doqsolv"
        description="Generate, manage, and deliver professional documents with template-based PDF generation."
      />
    );
  }

  if (fillerTemplateId) {
    return (
      <TemplateFiller
        templateId={fillerTemplateId}
        onClose={() => setFillerTemplateId(null)}
      />
    );
  }

  if (editorOpen) {
    return (
      <TemplateEditor
        template={editorTemplate}
        isAdmin={isAdmin}
        onClose={handleEditorClose}
        onSave={handleEditorSave}
      />
    );
  }

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-100 shadow-sm px-6 py-4 mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <svg className="w-6 h-6 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            DoqSolv — Document Management
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {isAdmin
              ? 'Manage documents, global defaults, and client templates.'
              : 'View generated documents, browse templates, or create your own.'}
          </p>
        </div>

        <button
          onClick={handleNew}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white shadow-sm transition"
          style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Template
        </button>
      </div>

      {/* Search + Tabs */}
      <div className="bg-white rounded-lg border border-gray-100 shadow-sm px-6 py-3 mb-4">
        <div className="flex items-center justify-between mb-3">
          <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
          <div className="relative w-64">
            <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search templates…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'documents' ? (
        <DocumentList isAdmin={isAdmin} />
      ) : loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      ) : displayedTemplates.length === 0 ? (
        <EmptyState
          message={
            searchQuery
              ? 'No templates match your search.'
              : activeTab === 'defaults'
              ? 'No default templates available yet.'
              : 'You have no templates yet. Create one or clone a default!'
          }
          actionLabel={!searchQuery ? '+ Create Template' : undefined}
          onAction={handleNew}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {displayedTemplates.map((t) => (
            <TemplateCard
              key={t.id}
              template={t}
              isAdmin={isAdmin}
              isGlobal={t.scope === 'global'}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onClone={handleClone}
              onPreview={setPreviewTemplate}
              onUse={setFillerTemplateId}
            />
          ))}
        </div>
      )}

      {/* Preview overlay */}
      {previewTemplate && (
        <PreviewOverlay template={previewTemplate} onClose={() => setPreviewTemplate(null)} />
      )}
    </div>
  );
};

export default DoqSolvPanel;
